import type { EmployerUser, Provider } from '~/types';
import { ShiftDifferentialType } from '~/types/hlx/enums';
import type { MsaasNegotiationSenderParty } from '~/types/hlx/msaasNegotiation';
import { COMP_TYPE_OPTIONS, SHIFT_DIFF_TYPE_OPTIONS } from './consts';
import type {
  NegotiationDraft,
  NegotiationDraftSource,
  NegotiationDraftTerms,
  NegotiationFormValues,
  NegotiationParty,
  NegotiationRecord,
  NegotiationResponseType,
  NegotiationViewMode,
} from './types';

type NegotiationLike = NegotiationRecord | NegotiationFormValues;
type JobListingWithDisplayNames = {
  employer?: { employerName?: string | null };
  employerName?: string | null;
  primaryFacility?: {
    facilityName?: string | null;
    employerFacility?: { facilityName?: string | null };
  };
  facilities?: Array<{
    facilityName?: string | null;
    employerFacility?: { facilityName?: string | null };
  }>;
};
type NegotiationViewer = {
  employerUser?: EmployerUser;
  provider?: Provider | null;
  viewerRole?: NegotiationParty;
};

type MsaasNegotiationParty = MsaasNegotiationSenderParty;

export const isSameNegotiationDraftSource = (
  left: NegotiationDraftSource | undefined,
  right: NegotiationDraftSource | undefined
): boolean => {
  if (!left || !right) return false;
  if ('responseId' in left || 'responseId' in right) {
    return 'responseId' in left && 'responseId' in right && left.responseId === right.responseId;
  }
  return left.listingType === right.listingType && left.listingId === right.listingId;
};

const getNegotiationResponseType = (negotiation: NegotiationLike): NegotiationResponseType => {
  if ('responseType' in negotiation) return negotiation.responseType;
  return 'msaasListingDetails' in negotiation ? 'msaas' : 'job';
};

export const getNegotiationSourceDetails = (negotiation: NegotiationLike) =>
  'msaasListingDetails' in negotiation
    ? negotiation.msaasListingDetails
    : negotiation.jobListingDetail;

export const getNegotiationListingTitle = (negotiation: NegotiationLike) =>
  'msaasListingDetails' in negotiation
    ? negotiation.msaasListing?.listingTitle
    : negotiation.jobListing?.jobTitle;

export const getNegotiationListingOwnerName = (negotiation: NegotiationLike) => {
  if ('msaasListingDetails' in negotiation) {
    return negotiation.listingEmployer?.employerName ?? 'Listing Employer';
  }
  const listing = negotiation.jobListing as JobListingWithDisplayNames | undefined;
  const facilityName =
    listing?.primaryFacility?.facilityName ??
    listing?.primaryFacility?.employerFacility?.facilityName ??
    listing?.facilities?.find((facility) => facility.facilityName)?.facilityName ??
    listing?.facilities?.find((facility) => facility.employerFacility?.facilityName)
      ?.employerFacility?.facilityName;
  return (
    listing?.employer?.employerName ?? listing?.employerName ?? facilityName ?? 'Listing Employer'
  );
};

export const getNegotiationResponderName = (negotiation: NegotiationLike) => {
  if ('msaasListingDetails' in negotiation) return negotiation.respondingEmployer?.employerName;
  const provider = negotiation.respondingProvider;
  return [provider?.firstName, provider?.lastName].filter(Boolean).join(' ') || 'Provider';
};

export const getNegotiationViewerRole = ({
  employerUser,
  provider,
  viewerRole,
}: NegotiationViewer): NegotiationParty | undefined => {
  if (viewerRole) return viewerRole;
  if (employerUser) return 'EMPLOYER';
  if (provider) return 'PROVIDER';
  return undefined;
};

/**
 * B2B negotiations have two employer sides. An EmployerUser's primary key is
 * not an Employer primary key, so it must never be used for this comparison.
 * If the viewer is not a party to this response, keep the editor read-only.
 */
export const getMsaasNegotiationViewerSide = (
  negotiation: NegotiationLike,
  viewer: NegotiationViewer
): MsaasNegotiationParty | undefined => {
  if (
    !('respondingEmployerId' in negotiation) ||
    !('listingEmployerId' in negotiation) ||
    viewer.employerUser?.employerId == null
  ) {
    return undefined;
  }

  const employerId = viewer.employerUser.employerId;
  if (negotiation.respondingEmployerId === employerId) return 'RESPONDER';
  if (negotiation.listingEmployerId === employerId) return 'LISTING_OWNER';
  return undefined;
};

const getMsaasNegotiationSenderParty = (
  negotiation: NegotiationLike
): MsaasNegotiationParty | undefined => {
  if (!('senderParty' in negotiation)) return undefined;
  return negotiation.senderParty === 'RESPONDER' || negotiation.senderParty === 'LISTING_OWNER'
    ? negotiation.senderParty
    : undefined;
};

export const isNegotiationAwaitingViewer = (
  negotiation: NegotiationLike,
  viewer: NegotiationViewer
) => {
  if (getNegotiationResponseType(negotiation) === 'job') {
    const role = getNegotiationViewerRole(viewer);
    return (
      !!role && 'sender' in negotiation && negotiation.sender != null && negotiation.sender !== role
    );
  }

  const viewerSide = getMsaasNegotiationViewerSide(negotiation, viewer);
  const senderParty = getMsaasNegotiationSenderParty(negotiation);
  return viewerSide != null && senderParty != null && senderParty !== viewerSide;
};

export const isNegotiationReadOnlyForViewer = (
  negotiation: NegotiationLike,
  viewer: NegotiationViewer
) => {
  if (getNegotiationResponseType(negotiation) === 'job') {
    const role = getNegotiationViewerRole(viewer);
    if (!role) return true;
    return 'sender' in negotiation && negotiation.sender != null && negotiation.sender === role;
  }

  const viewerSide = getMsaasNegotiationViewerSide(negotiation, viewer);
  const senderParty = getMsaasNegotiationSenderParty(negotiation);
  // Sender-side data is server-owned. Do not make a counter offer if the
  // caller cannot be matched to either employer or the response is an older
  // record without senderParty; the backend remains authoritative as well.
  return viewerSide == null || senderParty == null || senderParty === viewerSide;
};

export const getNegotiationTurnLabel = ({
  awaitingCurrentViewer,
  isLatestVersion,
  isReadOnly,
  isDraft,
  isSubmitting,
}: {
  awaitingCurrentViewer: boolean;
  isLatestVersion: boolean;
  isReadOnly: boolean;
  isDraft?: boolean;
  isSubmitting?: boolean;
}) => {
  if (isSubmitting) return 'Submitting Offer';
  if (isDraft) return 'Draft Offer';
  if (!isLatestVersion) return 'Review Terms';
  if (awaitingCurrentViewer) return 'Awaiting Your Response';
  return isReadOnly ? 'Awaiting Other Party' : 'Review Terms';
};

export const transformNegotiationApiToFormValues = (
  negotiation: NegotiationRecord,
  responseType: NegotiationResponseType = 'msaas'
): NegotiationFormValues => {
  const sourceDetails = getNegotiationSourceDetails(negotiation);
  if (!sourceDetails) {
    throw new Error('Negotiation response is missing listing details');
  }
  const compType = COMP_TYPE_OPTIONS.find((option) => option.value === negotiation.compType);
  const apiShiftDiffType = (negotiation.shiftDiffType ?? sourceDetails.shiftDiffType) as
    | string
    | null
    | undefined;
  // `shiftDiffType` is optional when a listing skips shift differentials. The
  // listing wizard represents that case with the incremental-rate default, so
  // use the same contract here. `DIFFERENTIAL` was emitted by an older client
  // but has never been a valid API enum; normalize it for historical records.
  const normalizedShiftDiffType =
    apiShiftDiffType == null || apiShiftDiffType === 'DIFFERENTIAL'
      ? ShiftDifferentialType.INCR_HOURLY_RATE
      : apiShiftDiffType;
  const shiftDiffType = SHIFT_DIFF_TYPE_OPTIONS.find(
    (option) => option.value === normalizedShiftDiffType
  );
  if (!compType || !shiftDiffType) {
    throw new Error('Negotiation response includes an unsupported contract option');
  }
  const msaasNegotiation = 'numOfFtes' in negotiation ? negotiation : undefined;
  const msaasSource = 'numOfFtes' in sourceDetails ? sourceDetails : undefined;

  const formValues: NegotiationFormValues = {
    ...negotiation,
    responseType,
    numOfFtes: msaasNegotiation?.numOfFtes ?? msaasSource?.numOfFtes ?? null,
    fteShiftsPerYear: msaasNegotiation?.fteShiftsPerYear ?? msaasSource?.fteShiftsPerYear ?? null,
    compType,
    pricePerHour: negotiation.pricePerHour ?? sourceDetails.pricePerHour,
    pricePerShift: negotiation.pricePerShift ?? sourceDetails.pricePerShift,
    pricePerRvu: negotiation.pricePerRvu ?? sourceDetails.pricePerRvu,
    yearlyRvusPerFte: msaasNegotiation?.yearlyRvusPerFte ?? msaasSource?.yearlyRvusPerFte ?? null,
    bonusesTxt: negotiation.bonusesTxt ?? sourceDetails.bonusesTxt ?? '',
    bonusesTxtFormatted: negotiation.bonusesTxt ?? sourceDetails.bonusesTxt,
    adminCostsTxt: negotiation.adminCostsTxt ?? sourceDetails.adminCostsTxt ?? '',
    adminCostsTxtFormatted: negotiation.adminCostsTxt ?? sourceDetails.adminCostsTxt,
    extraTxt: negotiation.extraTxt ?? sourceDetails.extraTxt ?? '',
    extraTxtFormatted: negotiation.extraTxt ?? sourceDetails.extraTxt,
    infrastructureTxt: negotiation.infrastructureTxt ?? sourceDetails.infrastructureTxt ?? '',
    infrastructureTxtFormatted: negotiation.infrastructureTxt ?? sourceDetails.infrastructureTxt,
    shiftDiffType,
    eveShiftRateDiff: negotiation.eveShiftRateDiff ?? sourceDetails.eveShiftRateDiff,
    nightShiftRateDiff: negotiation.nightShiftRateDiff ?? sourceDetails.nightShiftRateDiff,
    weDayShiftRateDiff: negotiation.weDayShiftRateDiff ?? sourceDetails.weDayShiftRateDiff,
    weEveShiftRateDiff: negotiation.weEveShiftRateDiff ?? sourceDetails.weEveShiftRateDiff,
    weNightShiftRateDiff: negotiation.weNightShiftRateDiff ?? sourceDetails.weNightShiftRateDiff,
    holDayShiftRateDiff: negotiation.holDayShiftRateDiff ?? sourceDetails.holDayShiftRateDiff,
    holEveShiftRateDiff: negotiation.holEveShiftRateDiff ?? sourceDetails.holEveShiftRateDiff,
    holNightShiftRateDiff: negotiation.holNightShiftRateDiff ?? sourceDetails.holNightShiftRateDiff,
    numDayShifts: negotiation.numDayShifts ?? sourceDetails.numDayShifts,
    numEveShifts: negotiation.numEveShifts ?? sourceDetails.numEveShifts,
    numNightShifts: negotiation.numNightShifts ?? sourceDetails.numNightShifts,
    numOfWeDayShifts: negotiation.numOfWeDayShifts ?? sourceDetails.numOfWeDayShifts,
    numOfWeEveShifts: negotiation.numOfWeEveShifts ?? sourceDetails.numOfWeEveShifts,
    numOfWeNightShifts: negotiation.numOfWeNightShifts ?? sourceDetails.numOfWeNightShifts,
    numHolDayShifts: negotiation.numHolDayShifts ?? sourceDetails.numHolDayShifts,
    numHolEveShifts: negotiation.numHolEveShifts ?? sourceDetails.numHolEveShifts,
    numHolNightShifts: negotiation.numHolNightShifts ?? sourceDetails.numHolNightShifts,
    startDate: negotiation.startDate ?? sourceDetails.startDate,
    endDate: negotiation.endDate ?? sourceDetails.endDate,
  };

  return formValues;
};

export const transformNegotiationsApiToFormValues = (
  negotiationStr: string,
  responseType: NegotiationResponseType = 'msaas'
): NegotiationFormValues[] => {
  const negotiations: NegotiationRecord[] = JSON.parse(negotiationStr);
  return negotiations.map((negotiation) =>
    transformNegotiationApiToFormValues(negotiation, responseType)
  );
};

// [MCV-1645] The nine shift buckets a MSaaS negotiation's calendar is split
// across. A JOB negotiation has no shift-distribution tab and never uses
// these -- see `buildNegotiationSchema`, which only runs this rule for
// `responseType === 'msaas'`.
export const SHIFT_ALLOCATION_FIELDS = [
  'numDayShifts',
  'numEveShifts',
  'numNightShifts',
  'numOfWeDayShifts',
  'numOfWeEveShifts',
  'numOfWeNightShifts',
  'numHolDayShifts',
  'numHolEveShifts',
  'numHolNightShifts',
] as const;

type ShiftAllocationValues = {
  [K in (typeof SHIFT_ALLOCATION_FIELDS)[number]]?: number | string | null;
} & {
  numOfFtes?: number | string | null;
  fteShiftsPerYear?: number | string | null;
};

const toFiniteNumber = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export const getAllocatedShifts = (values: ShiftAllocationValues): number =>
  SHIFT_ALLOCATION_FIELDS.reduce((sum, field) => sum + toFiniteNumber(values[field]), 0);

export const getExpectedShifts = (values: ShiftAllocationValues): number =>
  toFiniteNumber(values.numOfFtes) * toFiniteNumber(values.fteShiftsPerYear);

/**
 * [MCV-1645] Pure allocation-integrity check shared by the zod resolver (via
 * `buildNegotiationSchema`) and the Shift Distribution tab's live total. Null
 * (no error) both when nothing has been allocated yet (a brand-new draft) and
 * when the allocated total exactly matches FTEs * shifts-per-FTE-per-year.
 */
export const getShiftAllocationError = (values: ShiftAllocationValues): string | null => {
  const allocated = getAllocatedShifts(values);
  const expected = getExpectedShifts(values);
  if (allocated === 0 || allocated === expected) return null;
  return 'Total Shifts Allocated must equal Total Shifts';
};

// Draft payloads cross a strict model-column boundary. Pick known editable
// columns instead of trying to blacklist every relationship, calculated
// value, display-only field, or future client property that might be present
// on NegotiationFormValues.
const COMMON_NEGOTIATION_TERM_KEYS = [
  'startDate',
  'endDate',
  'numOfShifts',
  'pricePerRvu',
  'hoursPerShift',
  'pricePerShift',
  'pricePerHour',
  'numDayShifts',
  'numEveShifts',
  'numNightShifts',
  'numOfWeDayShifts',
  'numOfWeEveShifts',
  'numOfWeNightShifts',
  'numHolDayShifts',
  'numHolEveShifts',
  'numHolNightShifts',
  'compType',
  'laborReqType',
  'numRvusReq',
  'minRvusPerShift',
  'shiftDiffType',
  'eveShiftRateDiff',
  'nightShiftRateDiff',
  'weDayShiftRateDiff',
  'weEveShiftRateDiff',
  'weNightShiftRateDiff',
  'holDayShiftRateDiff',
  'holEveShiftRateDiff',
  'holNightShiftRateDiff',
  'bonusesTxt',
  'adminCostsTxt',
  'infrastructureTxt',
  'extraTxt',
] as const;

const MSAAS_NEGOTIATION_TERM_KEYS = [
  ...COMMON_NEGOTIATION_TERM_KEYS.filter((key) => key !== 'numOfShifts'),
  'numOfFtes',
  'fteShiftsPerYear',
  'yearlyRvusPerFte',
] as const;

export const JOB_NEGOTIATION_TERM_KEYS = [
  ...COMMON_NEGOTIATION_TERM_KEYS,
  'shiftPeriod',
  'shiftsPerPeriod',
  'callShiftsPerPeriod',
  'rvusPerPeriod',
  'rvuPeriod',
  'rvuBonusPeriod',
  'rvuBonusMinimum',
  'rvuBonusAmt',
  'callShiftDiffType',
  'eveCallShiftRateDiff',
  'nightCallShiftRateDiff',
  'weDayCallShiftRateDiff',
  'weEveCallShiftRateDiff',
  'weNightCallShiftRateDiff',
  'holDayCallShiftRateDiff',
  'holEveCallShiftRateDiff',
  'holNightCallShiftRateDiff',
  'pricePerCallShift',
  'numDayCallShifts',
  'numEveCallShifts',
  'numNightCallShifts',
  'numOfWeDayCallShifts',
  'numOfWeEveCallShifts',
  'numOfWeNightCallShifts',
  'numHolDayCallShifts',
  'numHolEveCallShifts',
  'numHolNightCallShifts',
] as const;

export const transformDraftTermsToApi = (
  values: NegotiationDraftTerms,
  responseType: NegotiationResponseType
): Record<string, unknown> => {
  const source = values as Record<string, unknown>;
  const allowedKeys =
    responseType === 'msaas' ? MSAAS_NEGOTIATION_TERM_KEYS : JOB_NEGOTIATION_TERM_KEYS;
  const apiValues: Record<string, unknown> = {};

  for (const key of allowedKeys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      apiValues[key] = source[key];
    }
  }

  apiValues.compType = values.compType?.value;
  apiValues.shiftDiffType = values.shiftDiffType?.value;
  apiValues.pricePerHour = values.pricePerHour || null;
  apiValues.pricePerShift = values.pricePerShift || null;
  apiValues.pricePerRvu = values.pricePerRvu || null;
  if (responseType === 'msaas') {
    apiValues.yearlyRvusPerFte = values.yearlyRvusPerFte || null;
  }

  return apiValues;
};

export type NegotiationDraftApiResponse = {
  listingType: 'JOB' | 'MSAAS';
  responseType: NegotiationResponseType;
  jobResponseId: number | null;
  listingId: number;
  listingTitle?: string | null;
  listingOwnerName?: string | null;
  responderName?: string | null;
  senderRole?: string;
  terms: Record<string, unknown>;
  // [MCV-1770] The listing's persisted totals, top-level because `terms` is
  // what the B2B apply summary renders and posts back.
  totalContractValue?: string | null;
  avgHourlyRate?: string | null;
};

/**
 * [MCV-1646] Maps the `negotiation-draft` GET response into a `NegotiationDraft`.
 * The endpoint returns bare `terms` (no relationship objects), so this feeds
 * `transformNegotiationApiToFormValues` a synthetic record shaped like a real
 * negotiation row -- `{ ...terms, jobListingDetail: terms }` for JOB,
 * `{ ...terms, msaasListingDetails: terms }` for MSaaS -- then discards the
 * synthetic key and any id/version/sender fields a draft must never carry.
 */
export const transformDraftApiToDraft = (
  apiDraft: NegotiationDraftApiResponse,
  source: NegotiationDraftSource,
  providerIds?: number[]
): NegotiationDraft => {
  const synthetic =
    apiDraft.responseType === 'job'
      ? { ...apiDraft.terms, jobListingDetail: apiDraft.terms }
      : { ...apiDraft.terms, msaasListingDetails: apiDraft.terms };

  const formValues = transformNegotiationApiToFormValues(
    synthetic as unknown as NegotiationRecord,
    apiDraft.responseType
  );

  const terms = { ...formValues } as Partial<NegotiationFormValues> & Record<string, unknown>;
  delete terms.jobListingDetail;
  delete terms.msaasListingDetails;
  delete terms.id;
  delete terms.version;
  delete terms.sender;
  delete terms.senderParty;
  // [MCV-1770] Previous column's totals. They never go out again: the submit
  // payload is built from an allowlist (`transformDraftTermsToApi`).
  const totals: Record<string, string> = {};
  if (apiDraft.totalContractValue != null) totals.totalContractValue = apiDraft.totalContractValue;
  if (apiDraft.avgHourlyRate != null) totals.avgHourlyRate = apiDraft.avgHourlyRate;

  return {
    kind: 'draft',
    responseType: apiDraft.responseType,
    source,
    providerIds,
    listingTitle: apiDraft.listingTitle ?? undefined,
    listingOwnerName: apiDraft.listingOwnerName,
    responderName: apiDraft.responderName,
    senderRole: apiDraft.senderRole,
    terms: { ...terms, ...totals } as NegotiationDraftTerms,
  };
};

/**
 * [MCV-1646] Single source of truth for which panel `NegotiationDetails`
 * renders. Order matters: `loading` and `submitting` pre-empt everything else,
 * a load failure only wins when there is no draft to fall back to, and a draft
 * always wins over an (impossible in practice, but defensive) empty
 * `negotiation`.
 */
export const getNegotiationViewMode = ({
  loading,
  loadFailed,
  draft,
  submitting,
  negotiation,
  latestVersion,
  isReadOnly,
}: {
  loading: boolean;
  loadFailed: boolean;
  draft: NegotiationDraft | undefined;
  submitting: boolean;
  negotiation: NegotiationFormValues | undefined;
  latestVersion: number;
  isReadOnly: boolean;
}): NegotiationViewMode => {
  if (loading) return 'loading';
  if (loadFailed && !draft) return 'error';
  if (submitting) return 'submitting';
  if (draft) return 'draft';
  if (!negotiation) return 'empty';
  if (negotiation.version !== latestVersion) return 'history';
  if (isReadOnly) return 'waiting';
  return 'actionable';
};

export const transformFormValuesToApi = (formValues: NegotiationFormValues) => {
  const apiValues = { ...formValues } as Partial<NegotiationFormValues> & Record<string, unknown>;
  delete apiValues.responseType;
  delete apiValues.senderParty;
  delete apiValues.bonusesTxtFormatted;
  delete apiValues.adminCostsTxtFormatted;
  delete apiValues.extraTxtFormatted;
  delete apiValues.infrastructureTxtFormatted;
  return JSON.stringify({
    ...apiValues,
    compType: formValues.compType.value,
    shiftDiffType: formValues.shiftDiffType.value,
    pricePerHour: formValues.pricePerHour || null,
    pricePerShift: formValues.pricePerShift || null,
    pricePerRvu: formValues.pricePerRvu || null,
    yearlyRvusPerFte: formValues.yearlyRvusPerFte || null,
  });
};
